import { useEffect, useRef, useState } from 'react';
import { GripVertical, Trash2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

/** One checklist item, normalized so the same editor serves shared + personal tasks. */
export interface ChecklistEditorEntry {
  id: string;
  title: string;
  done: boolean;
}

interface ChecklistEditorProps {
  items: ChecklistEditorEntry[];
  /** Done / total counts shown in the label. */
  doneCount: number;
  totalCount: number;
  /** Toggle an item; `done` is its CURRENT state (caller decides the next). */
  onToggle: (id: string, done: boolean) => void;
  onDelete: (id: string) => void;
  onAdd: (title: string) => void;
  /** Persist a new order (ids top→bottom). Omit to disable reordering. */
  onReorder?: (order: string[]) => void;
  /**
   * Whether the caller may toggle/delete/reorder EXISTING items. Adding is always
   * allowed. Reviewers annotating an in-review task get `false` — they can add a
   * "fix this" checklist item but not check off, remove, or reorder existing ones.
   */
  canModifyExisting?: boolean;
}

/**
 * The checklist editor shared by EditItemDialog (shared list) and
 * EditPersonalItemDialog (personal overlay). Presentational: it owns the ephemeral
 * "add" input plus a local drag mirror; toggle/delete/add/reorder are delegated so
 * each dialog can wire its own api hook (useTodoChecklist vs usePersonalChecklist).
 */
export function ChecklistEditor({
  items,
  doneCount,
  totalCount,
  onToggle,
  onDelete,
  onAdd,
  onReorder,
  canModifyExisting = true,
}: ChecklistEditorProps) {
  const [newItem, setNewItem] = useState('');
  // Local mirror so a drag reflects instantly; re-synced from props when idle.
  const [ordered, setOrdered] = useState(items);
  const draggingRef = useRef(false);
  useEffect(() => {
    if (!draggingRef.current) setOrdered(items);
  }, [items]);

  const canReorder = canModifyExisting && !!onReorder && ordered.length > 1;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  function handleDragEnd(e: DragEndEvent) {
    draggingRef.current = false;
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = ordered.findIndex((s) => s.id === active.id);
    const newIndex = ordered.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(ordered, oldIndex, newIndex);
    setOrdered(next);
    onReorder?.(next.map((s) => s.id));
  }

  function addItem() {
    const t = newItem.trim();
    if (!t) return;
    onAdd(t);
    setNewItem('');
  }

  return (
    <div className="space-y-2">
      <Label>Checklist{totalCount > 0 ? ` (${doneCount}/${totalCount})` : ''}</Label>
      <div className="max-h-64 space-y-1 overflow-y-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={() => {
            draggingRef.current = true;
          }}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            draggingRef.current = false;
          }}
        >
          <SortableContext items={ordered.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            {ordered.map((entry) => (
              <ChecklistRow
                key={entry.id}
                entry={entry}
                canReorder={canReorder}
                canModifyExisting={canModifyExisting}
                onToggle={onToggle}
                onDelete={onDelete}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder="Add a checklist item..."
          className="h-8 text-sm"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addItem();
            }
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addItem}
          disabled={!newItem.trim()}
        >
          Add
        </Button>
      </div>
    </div>
  );
}

function ChecklistRow({
  entry,
  canReorder,
  canModifyExisting,
  onToggle,
  onDelete,
}: {
  entry: ChecklistEditorEntry;
  canReorder: boolean;
  canModifyExisting: boolean;
  onToggle: (id: string, done: boolean) => void;
  onDelete: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.id,
    disabled: !canReorder,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-2">
      {canReorder && (
        <button
          type="button"
          className="mt-1 flex-shrink-0 cursor-grab touch-none text-muted-foreground/50 hover:text-muted-foreground"
          aria-label={`Reorder checklist item "${entry.title}"`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      <Checkbox
        checked={entry.done}
        onCheckedChange={() => onToggle(entry.id, entry.done)}
        disabled={!canModifyExisting}
        aria-label={`Mark "${entry.title}" ${entry.done ? 'not done' : 'done'}`}
        className="mt-1 flex-shrink-0"
      />
      {canModifyExisting ? (
        <span
          role="button"
          tabIndex={0}
          onClick={() => onToggle(entry.id, entry.done)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle(entry.id, entry.done);
            }
          }}
          className={`min-w-0 flex-1 cursor-pointer whitespace-pre-wrap break-words text-sm ${
            entry.done ? 'line-through text-muted-foreground' : ''
          }`}
        >
          {entry.title}
        </span>
      ) : (
        <span
          className={`min-w-0 flex-1 whitespace-pre-wrap break-words text-sm ${
            entry.done ? 'line-through text-muted-foreground' : ''
          }`}
        >
          {entry.title}
        </span>
      )}
      {canModifyExisting && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-destructive"
          onClick={() => onDelete(entry.id)}
          aria-label={`Delete checklist item "${entry.title}"`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
